import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import Modal, { type ModalRef } from '@/components/@IntranetLibrary/Modal';
import MobileBottomSheet from '@/components/@IntranetLibrary/MobileBottomSheet';
import SerpContentSnapshot from '@/components/Approval/@components/SerpContentSnapshot';
import type { ElementTransform } from '@/components/Common/HtmlToReact';
import useLayoutSize from '@/hooks/useLayoutSize';
import {
  DETAIL_BUTTON_CLASS,
  DETAIL_CONTENT_ID,
  DETAIL_MODAL_ID,
  DETAIL_TITLE_ID,
} from './content';
import {
  MarketingCaseDetailRoot,
  MarketingCaseRoot,
  MarketingCaseSheetBody,
} from './styles';

export { buildMarketingCaseContentHtml } from './content';

const LEGACY_DETAIL_BUTTON_CLASS = 'btn_detail';

type DetailPart = { title: string; body: string };

const readDetailModal = (content: string): DetailPart => {
  const doc = new DOMParser().parseFromString(content, 'text/html');
  return {
    title: doc.getElementById(DETAIL_TITLE_ID)?.textContent?.trim() ?? '',
    body: doc.getElementById(DETAIL_CONTENT_ID)?.innerHTML ?? '',
  };
};

type DetailDialogProps = DetailPart & { open: boolean; onClose: () => void };

/** 「상세보기」 — PC 모달, 모바일 바텀시트 */
const DetailDialog = ({ open, onClose, title, body }: DetailDialogProps) => {
  const { t } = useTranslation();
  const { isMobile } = useLayoutSize();
  const modalRef = useRef<ModalRef>(null);
  const dialogTitle = title || t('견적 상세내역');

  useEffect(() => {
    if (isMobile) return;
    if (open) modalRef.current?.open();
    else modalRef.current?.close();
  }, [open, isMobile]);

  const content = (
    <MarketingCaseDetailRoot>
      <SerpContentSnapshot content={body} />
    </MarketingCaseDetailRoot>
  );

  if (isMobile) {
    return (
      <MobileBottomSheet
        open={open}
        onClose={onClose}
        title={dialogTitle}
        height="auto"
        maxHeight="80vh"
        swipeable={false}
      >
        <MarketingCaseSheetBody>{content}</MarketingCaseSheetBody>
      </MobileBottomSheet>
    );
  }
  return (
    <Modal ref={modalRef} title={dialogTitle} handleClose={onClose}>
      {content}
    </Modal>
  );
};

/** 저장 content 그대로 — 「상세보기」는 저장된 견적 상세를 포탈 모달로 */
export const MarketingCaseSnapshot = ({ content }: { content: string }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const detail = useMemo(() => readDetailModal(content), [content]);

  const transform = useCallback<ElementTransform>(
    ({ element, props }) => {
      if (element.id === DETAIL_MODAL_ID) return null;
      // 레거시 문서 표의 버튼 — 누를 때 조회하던 상세 내용이 저장돼 있지 않음
      if (element.classList.contains(LEGACY_DETAIL_BUTTON_CLASS)) return null;
      if (element.classList.contains(DETAIL_BUTTON_CLASS)) {
        return (
          <BoxButton
            key={props.key as string}
            type="button"
            variant="secondary"
            size="small"
            onClick={() => setOpen(true)}
          >
            {t('상세보기')}
          </BoxButton>
        );
      }
      return undefined;
    },
    [t],
  );

  return (
    <MarketingCaseRoot>
      <SerpContentSnapshot content={content} transform={transform} />
      <DetailDialog
        open={open}
        onClose={() => setOpen(false)}
        title={detail.title}
        body={detail.body}
      />
    </MarketingCaseRoot>
  );
};
